import LoadingButton from '@mui/lab/LoadingButton'
import {Box, FormControl, InputBase} from '@mui/material'
import {Typography} from '@viptech/react-components'
import {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import AppVersion from 'src/components/AppVersion'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import api from 'src/services/api'
import createStyleSheet from 'src/utilities/createStyleSheet'
import {isEmailLoginValid} from 'src/utilities/regexValidation'
import Loading from '../../components/Loading'
import useLoading from '../../hooks/useLoading'
import BrainLoginThumb from '../../images/brain-login-image.png'
import BrainLoginVideo from '../../images/brain-login-video.mp4'
import {AxiosErrorGeneric} from '../../utilities/RequestError'
import {BlendDiv, Logo, VideoContainer} from '../login/Styles'
import {ButtonDiv, CancelAndGoBackButton, Container, Form, InputDiv, LoginBox, ScreenContainer, TopDiv} from './Styles'
import FeedbackForgotPassword from './components/FeedbackForgotPassword'

function ForgotPassword() {
  const [email, setEmail] = useState<string>('')
  const [renderFeedback, setRenderFeedback] = useState<boolean>(false)
  const [responseOk, setResponseOk] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const {loading, applyLoading} = useLoading(false)
  const {loginPagesLogo} = usePersonalizedThemeByCompany()

  const navigate = useNavigate()

  async function handleSubmit() {
    try {
      await applyLoading(async () => await api.auth.forgotPassword(email))

      setResponseOk(true)
    } catch (error) {
      const err = error as AxiosErrorGeneric
      setResponseOk(false)
      setErrorMessage(err.response.data.message)
    } finally {
      setRenderFeedback(true)
    }
  }

  async function resendEmail() {
    try {
      await api.auth.resendMail(email, 'forgot-password')
      setResponseOk(true)
    } catch (error) {
      setResponseOk(false)
    } finally {
      setRenderFeedback(true)
    }
  }

  function handleKeyPress(e: any) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (isEmailLoginValid(email)) handleSubmit()
    }
  }

  return (
    <ScreenContainer>
      <BlendDiv />
      <Container>
        {renderFeedback ? (
          <FeedbackForgotPassword
            email={email}
            resendFunction={resendEmail}
            success={responseOk}
            tryAnotherEmail={() => setRenderFeedback(false)}
            errorMessage={errorMessage}
          />
        ) : (
          <LoginBox>
            <TopDiv>
              <Logo src={loginPagesLogo} />
              <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px', alignSelf: 'flex-start'}}>
                <Typography variant='label' color={'#F2F4F7'} size='22px'>
                  Esqueceu sua senha
                </Typography>
                <Typography variant='label' size='16px' color='#EAECF0'>
                  Insira seu e-mail para redefinir a senha da sua conta
                </Typography>
              </Box>
            </TopDiv>
            <Form style={{gap: '24px'}}>
              <InputDiv>
                <FormControl>
                  <Box sx={{width: '100%', flexDirection: 'column', display: 'flex', flex: 1, gap: '8px'}}>
                    <Typography variant='label' htmlFor='email' color='#F2F4F7' size='16px'>
                      E-mail
                    </Typography>
                    <InputBase
                      id='email'
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      sx={styles.inputPassword}
                      autoFocus={true}
                      onKeyPress={(event) => handleKeyPress(event)}
                    />
                  </Box>
                </FormControl>
              </InputDiv>
              <ButtonDiv>
                <LoadingButton
                  variant='contained'
                  sx={styles.confirmButton}
                  disabled={!isEmailLoginValid(email)}
                  onClick={() => handleSubmit()}
                  loading={loading}
                  loadingIndicator={<Loading color='#fff' width='15px' height='15px' />}>
                  Enviar
                </LoadingButton>
                <CancelAndGoBackButton onClick={() => navigate('/login')}>Voltar para login</CancelAndGoBackButton>
              </ButtonDiv>
            </Form>
          </LoginBox>
        )}
      </Container>
      <AppVersion color='white' />

      <VideoContainer muted autoPlay loop preload='auto' src={BrainLoginVideo} poster={BrainLoginThumb}></VideoContainer>
    </ScreenContainer>
  )
}

const styles = createStyleSheet({
  confirmButton: {
    width: '100%',
    height: '50px',
    fontFamily: 'Inter',
    fontSize: '16px',
    boxShadow: 'none',
    textTransform: 'none',
    marginBottom: '15px',
    borderRadius: '12px',
    '&:disabled': {
      background: '#98A2B3',
      color: '#FFFFFF',
    },
  },
  inputPassword: {
    fontSize: '16px',
    color: '#ffffff',
    backgroundColor: '#344054',
    height: '3.5em',
    borderRadius: '10px',
    padding: '0px 15px 0px 15px',
    border: 'none',
    textIndent: '4%',
    outline: '0',
    '&:focus': {
      outline: 'none',
    },
  },
})

export default ForgotPassword
